import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, LayoutGroup } from "framer-motion";
import { cn } from "@/lib/utils";
import { NAV_SECTIONS } from "@/config/nav";
import { useApp } from "@/context/AppContext";
import { SPRING } from "@/motion";
import { BrandLogo } from "@/components/common/BrandIdentity";
import { initials } from "@/lib/format";
import { ChevronsLeft, ChevronsRight, ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function Sidebar({ collapsed, onToggle }) {
  const { persona, personaRole, can, logout } = useApp();
  const navigate = useNavigate();

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 74 : 248 }}
      transition={SPRING.panel}
      className="flex h-screen flex-col overflow-hidden bg-navy text-navy-text"
      data-testid="app-sidebar"
    >
      {/* Brand */}
      <div className={cn("flex items-center gap-2 px-4 py-4", collapsed && "justify-center px-0")}>
        <BrandLogo className="h-11 w-10" testId="brand-logo" />
        {!collapsed && (
          <div className="brand-wordmark leading-tight">
            <div className="font-display text-xl font-semibold tracking-[0.12em] text-gold">NYAYYAKSHA</div>
            <div className="text-[10px] tracking-wide text-gold/70">न्यायः परमः धर्मः</div>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="nyk-scroll flex-1 overflow-y-auto px-2 py-2">
        <LayoutGroup id="sidebar-nav">
        {NAV_SECTIONS.map((section) => {
          const items = section.items.filter((i) => can(i.perm) && !(i.hideForRoles || []).includes(personaRole));
          if (!items.length) return null;
          return (
            <div key={section.label} className="mb-4">
              {!collapsed && (
                <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-navy-text/50">
                  {section.label}
                </div>
              )}
              <div className="space-y-0.5">
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    title={item.label}
                    data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      cn(
                        "group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150",
                        collapsed && "justify-center px-0",
                        isActive ? "text-white" : "text-navy-text hover:bg-navy-hover hover:text-white"
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <motion.span
                            layoutId="sidebar-active-marker"
                            className="absolute inset-0 rounded-md bg-navy-active"
                            transition={SPRING.control}
                          />
                        )}
                        <item.icon className="relative z-10 h-[18px] w-[18px] shrink-0" />
                        {!collapsed && <span className="relative z-10">{item.label}</span>}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
        </LayoutGroup>
      </nav>

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        data-testid="sidebar-collapse-toggle"
        className={cn(
          "mx-2 mb-2 flex items-center gap-2 rounded-md px-3 py-2 text-xs text-navy-text/70 hover:bg-navy-hover hover:text-white",
          collapsed && "justify-center px-0"
        )}
      >
        {collapsed ? <ChevronsRight className="h-4 w-4" /> : <><ChevronsLeft className="h-4 w-4" /> Collapse</>}
      </button>

      {/* Profile */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="sidebar-profile"
            className={cn(
              "flex items-center gap-3 border-t border-white/10 px-3 py-3 text-left hover:bg-navy-hover",
              collapsed && "justify-center px-0"
            )}
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-copper text-sm font-semibold text-white">
              {initials(persona.name)}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-white">{persona.name}</div>
                <div className="truncate text-[11px] text-navy-text/70">{persona.role}</div>
              </div>
            )}
            {!collapsed && <ChevronDown className="h-4 w-4 text-navy-text/60" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" className="w-56">
          <DropdownMenuLabel>
            <div className="font-medium">{persona.name}</div>
            <div className="text-xs font-normal text-ink-2">{persona.org}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/settings")}>Profile</DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/settings")}>Organisation</DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/settings")}>Switch Role / Demo Persona</DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/settings")}>Preferences</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="logout-menu-item"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="text-danger"
          >
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </motion.aside>
  );
}
